import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  ArrowUpRight,
  Sparkles,
  LayoutTemplate,
  Gem,
  LineChart,
  Cpu,
} from 'lucide-react';
import { Section, SectionHeading } from '@/components/layout/Section';
import { Reveal } from '@/components/layout/Reveal';
import { TextLink } from '@/components/site/CtaButton';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const ICONS = { Sparkles, LayoutTemplate, Gem, LineChart, Cpu };

/* --------------------------------------------------------------- one card */

/**
 * A single service, presented as a wide editorial row.
 *
 * The cinema here is scroll-linked but never scroll-hijacking: the card drifts
 * vertically at its own rate, and the one closest to the middle of your screen
 * is the only one fully lit — the others sit slightly back and slightly dimmed.
 * Because everything is driven by the card's own position (not by measuring
 * the viewport height), it behaves identically on a 13" laptop and a 27"
 * monitor.
 */
const ServiceRow = ({ service, index, onFocusChange }) => {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const rowRef = useRef(null);
  const Icon = ICONS[service.icon] || Sparkles;

  const { scrollYProgress } = useScroll({
    target: rowRef,
    offset: ['start end', 'end start'],
  });

  // 0 = just entering from the bottom, 0.5 = dead centre, 1 = leaving the top.
  const y = useTransform(scrollYProgress, [0, 1], [34, -34]);
  const numeralY = useTransform(scrollYProgress, [0, 1], [56, -56]);
  const scale = useTransform(
    scrollYProgress,
    [0.16, 0.42, 0.58, 0.84],
    [0.978, 1, 1, 0.978],
  );
  const opacity = useTransform(
    scrollYProgress,
    [0.16, 0.42, 0.58, 0.84],
    [0.62, 1, 1, 0.62],
  );

  // Tell the sticky column which service is currently in focus. A 12% band in
  // the middle of the screen means exactly one row can own it at a time.
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onFocusChange(index);
      },
      { rootMargin: '-44% 0px -44% 0px', threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [index, onFocusChange]);

  return (
    <motion.div
      ref={rowRef}
      style={reduce ? undefined : { y, scale, opacity }}
      className={cn('relative', !reduce && 'rmh-willchange')}
    >
      <Link
        to={`/services#${service.slug}`}
        data-testid={`home-service-card-${service.slug}`}
        className={cn(
          'group relative block overflow-hidden rounded-[18px] border p-7 sm:p-9 lg:p-10',
          'border-[color:var(--rmh-border)] transition-[box-shadow,border-color] duration-500',
          'hover:border-[color:var(--rmh-border-strong)] focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)] focus-visible:ring-offset-2',
        )}
        style={{
          backgroundColor: 'var(--rmh-surface)',
          boxShadow: 'var(--shadow-hairline)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = 'var(--shadow-soft)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'var(--shadow-hairline)';
        }}
      >
        {/* hairline that lights up on hover */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ backgroundColor: 'var(--rmh-accent)' }}
        />

        {/* oversized counter, cropped by the card */}
        <motion.span
          aria-hidden="true"
          style={reduce ? undefined : { y: numeralY }}
          className="rmh-num rmh-numeral-ghost pointer-events-none absolute -right-3 -top-10 select-none text-[7rem] sm:-right-4 sm:text-[9rem] lg:text-[10.5rem]"
        >
          {service.number}
        </motion.span>

        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-8">
          <div className="flex items-center gap-4 sm:flex-col sm:items-start sm:gap-5">
            <span
              className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 group-hover:border-[color:var(--rmh-accent)]"
              style={{
                backgroundColor: 'var(--rmh-surface-inset)',
                borderColor: 'var(--rmh-border)',
              }}
            >
              <Icon
                className="h-[19px] w-[19px]"
                strokeWidth={1.4}
                style={{ color: 'var(--rmh-mocha)' }}
                aria-hidden="true"
              />
            </span>
            <span className="rmh-num text-[0.7rem] tracking-[0.12em] text-[color:var(--rmh-muted-2)]">
              {service.number}
            </span>
          </div>

          <div>
            <h3 className="font-serif text-[1.32rem] leading-snug text-[color:var(--rmh-ink)] transition-transform duration-500 group-hover:translate-x-[2px] sm:text-[1.55rem] lg:text-[1.7rem]">
              {service.title}
            </h3>
            <p className="rmh-body mt-3.5 max-w-2xl text-[0.9rem] sm:text-[0.95rem]">
              {service.short}
            </p>
            <span className="mt-7 inline-flex items-center gap-2 text-[0.74rem] font-medium uppercase tracking-[0.16em] text-[color:var(--rmh-ink)]">
              <span className="rmh-link-underline">{t.common.learnMore}</span>
              <ArrowUpRight
                className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ------------------------------------------------------------ the section */

export const HomeServicesSticky = () => {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const services = t.services;
  const columnRef = useRef(null);
  const [active, setActive] = useState(0);

  const { scrollYProgress } = useScroll({
    target: columnRef,
    offset: ['start 80%', 'end 60%'],
  });
  const railFill = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const handleFocusChange = React.useCallback((index) => {
    setActive((prev) => (prev === index ? prev : index));
  }, []);

  const goTo = (slug) => {
    const el = document.getElementById(`home-service-${slug}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Section id="services" tone="canvas" wide data-testid="home-services-section">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
        {/* ------------------------------ sticky editorial column --------- */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <SectionHeading
                eyebrow={t.home.services.eyebrow}
                title={t.home.services.title}
                lede={t.home.services.lede}
                titleClassName="text-[1.75rem] sm:text-[2.1rem] lg:text-[2.35rem]"
              />
            </Reveal>

            {/* reel index — desktop only, where the column actually pins */}
            <div
              className="mt-9 hidden items-stretch gap-4 lg:flex"
              data-testid="home-services-reel-index"
            >
              <div
                className="relative w-px shrink-0"
                style={{ backgroundColor: 'var(--rmh-border-strong)' }}
                role="presentation"
              >
                <motion.span
                  aria-hidden="true"
                  style={
                    reduce
                      ? { scaleY: 1, transformOrigin: 'top' }
                      : { scaleY: railFill, transformOrigin: 'top' }
                  }
                  className="rmh-willchange absolute inset-0 block"
                >
                  <span
                    className="block h-full w-px"
                    style={{ backgroundColor: 'var(--rmh-accent)' }}
                  />
                </motion.span>
              </div>

              <ol className="flex flex-col gap-3.5">
                {services.map((s, i) => {
                  const isActive = active === i;
                  return (
                    <li key={s.slug}>
                      <button
                        type="button"
                        onClick={() => goTo(s.slug)}
                        aria-current={isActive ? 'true' : undefined}
                        data-testid={`home-services-index-${s.slug}-button`}
                        className="group flex items-baseline gap-3 text-left focus-visible:outline-none"
                      >
                        <span
                          className={cn(
                            'rmh-num text-[0.62rem] tracking-[0.14em] transition-colors duration-300',
                            isActive
                              ? 'text-[color:var(--rmh-accent-2)]'
                              : 'text-[color:var(--rmh-muted-2)]',
                          )}
                        >
                          {s.number}
                        </span>
                        <span
                          className={cn(
                            'font-serif text-[0.95rem] leading-snug transition-colors duration-300',
                            isActive
                              ? 'text-[color:var(--rmh-ink)]'
                              : 'text-[color:var(--rmh-muted-2)] group-hover:text-[color:var(--rmh-ink-2)]',
                          )}
                        >
                          {s.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="mt-9">
              <TextLink to="/services" testId="home-services-view-all-link">
                {t.common.viewAllServices}
              </TextLink>
            </div>
          </div>
        </div>

        {/* --------------------------------------- the drifting rows ------- */}
        <div
          ref={columnRef}
          className="flex flex-col gap-6 lg:col-span-8 lg:gap-7"
        >
          {services.map((service, i) => (
            <div key={service.slug} id={`home-service-${service.slug}`}>
              <ServiceRow
                service={service}
                index={i}
                onFocusChange={handleFocusChange}
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default HomeServicesSticky;
